import { Area } from '../domain/Area'
import { Availability } from '../domain/Availability'
import { AvailabilityService } from '../services/availability.service'
import { BookingDetails, BookingType } from '../domain/BookingDetails'
import { BookingReason } from '../domain/BookingReason'
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, booleanAttribute, } from '@angular/core'
import { ControlContainer, FormGroup, FormGroupDirective } from '@angular/forms'
import { DurationUnit } from '../pipes/duration.pipe'
import { Event } from '../domain/Event'
import { FormStep } from '../booking-form/booking-form.component'
import { NGBDateTimeAdapter } from '../utilities/NgbDateTimeAdapter'
import {
    NgbDate,
    NgbDateNativeAdapter,
    NgbDatepickerConfig,
    NgbDatepickerI18n,
    NgbDateStruct
} from '@ng-bootstrap/ng-bootstrap'
import { EMPTY, catchError, distinctUntilChanged, forkJoin, map, of, Subject, Subscription, takeUntil, tap, throwError } from 'rxjs'
import { environment } from '../../environments/environment'
import { GBTimeZoneDatepickerI18n } from '../services/gb-time-zone-datepicker-i18n.service'
import { Helper } from '../utilities/helper'
import { BookingSlot } from '../domain/BookingSlot'
import { BookingAction, LoggingService } from '../services/logging.service'
import { VenueOpenDaysCalendar } from '../domain/VenueOpenDaysCalendar'
import { isVenueNotInServiceError } from '../services/venue-not-in-service-error'

enum ModifyBookingStep {
    Summary,
    SelectRestrictions,
    SelectTableAndDate,
    SelectBookingType,
    ExplainNonReservableEventBooking,
    SelectReason,
    SelectDuration,
    SelectArea,
    SelectTime,
    SelectOrder,
    JoinWaitlist,
}

@Component({
    selector: 'app-modal-booking-form',
    templateUrl: './modal-booking-form.component.html',
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective,
        },
    ],
    providers: [
        {
            provide: NgbDatepickerI18n,
            useClass: GBTimeZoneDatepickerI18n,
        }
    ],
})
export class ModalBookingFormComponent implements OnInit, OnDestroy {

    @Input() organisationId!: string
    @Input() businessId!: string
    @Input() venueId!: string
    @Input() bookingDetails?: BookingDetails
    @Input() restrictedReasonIds?: string[]
    @Input() preselectedEventId?: string
    @Input() preselectedAreaId?: string
    @Input() initialIdealTime?: Date
    @Input({ transform: booleanAttribute }) hideDateTimeControls = false
    @Input({ transform: booleanAttribute }) hidePinnedEvent = false
    @Output() saveRequested = new EventEmitter<[FormGroup, Availability]>()
    form!: FormGroup
    earliestDate: NgbDateStruct
    availability: Availability | null = null
    minLargePartySize: number | null = null
    partySizePluralMapping = {
        '=1': '1 person',
        other: '# people',
    }
    venueColour?: string | null
    venueNotInService = false
    hasHadChanceToViewPinnedEvent: boolean | null = null
    openDaysCalendar = new VenueOpenDaysCalendar()
    isDateDisabled: (date: NgbDate, current?: { year: number; month: number }) => boolean
    protected readonly ModifyBookingStep = ModifyBookingStep
    protected ngbTimeAdapter = new NGBDateTimeAdapter()
    private ngbDateAdapter = new NgbDateNativeAdapter()
    private onDestroy$ = new Subject<void>()
    private goNextStepAfterGettingAvailabilitySubscription: Subscription | null = null
    private updateIdealTimeDateAndAdjustToAvailabilitySubscription: Subscription | null = null

    get formDate(): Date | null {
        return this.getFormDate()
    }

    get formIdealTime(): Date | null {
        return this.form?.get('idealTime')?.value ?? null
    }

    constructor(
        private availabilityService: AvailabilityService,
        private rootFormGroup: FormGroupDirective,
        private datepickerConfig: NgbDatepickerConfig,
        private loggingService: LoggingService
    ) {
        this.earliestDate = this.ngbDateAdapter.fromModel(new Date())!
        this.isDateDisabled = (date: NgbDate) => {
            return this.openDaysCalendar.isDateClosed(date.year, date.month, date.day)
        }
    }

    ngOnInit() {
        let formName = FormStep[FormStep.BookingSlot]
        this.form = this.rootFormGroup.form.get(formName) as FormGroup
        this.setFormToBookingDetailsIfNotAlreadySet()
        this.bindFormDateToGettingAvailability()
        this.bindFormTimeToGoingToNextStep()
        const initialDate = this.getFormDate() ?? new Date()
        this.loadInitialOpenDaysThenRefreshAvailability(initialDate)
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    selectTableAndDateStep() {
        this.form.get('step')!.setValue(ModifyBookingStep.SelectTableAndDate)
        this.form.get('requiresWheelchairAccess')!.setValue(null)
        if (this.form.get('selectedEventId')!.enabled) {
            this.form.get('bookingType')!.setValue(null)
            this.form.get('selectedEventId')!.setValue(null)
        }
        if (this.form.get('selectedReasonId')!.enabled) {
            this.form.get('selectedReasonId')!.setValue(null)
        }
        this.form.get('selectedAreaId')!.setValue(null)
        this.form.get('time')!.setValue(null)
    }

    selectRestrictionsStep() {
        this.form.get('step')!.setValue(ModifyBookingStep.SelectRestrictions)
        if (this.form.get('selectedEventId')!.enabled) {
            this.form.get('bookingType')!.setValue(null)
            this.form.get('selectedEventId')!.setValue(null)
        }
        if (this.form.get('selectedReasonId')!.enabled) {
            this.form.get('selectedReasonId')!.setValue(null)
        }
        this.form.get('selectedAreaId')!.setValue(null)
        this.form.get('time')!.setValue(null)    }

    selectBookingTypeStep() {
        this.form.get('step')!.setValue(ModifyBookingStep.SelectBookingType)
        if (this.form.get('selectedEventId')!.enabled) {
            this.form.get('bookingType')!.setValue(null)
            this.form.get('selectedEventId')!.setValue(null)
        }
        if (this.form.get('selectedReasonId')!.enabled) {
            this.form.get('selectedReasonId')!.setValue(null)
        }
        this.form.get('selectedAreaId')!.setValue(null)
        this.form.get('time')!.setValue(null)
    }

    selectReasonStep() {
        this.form.get('step')!.setValue(ModifyBookingStep.SelectReason)
        if (this.form.get('selectedEventId')!.enabled) {
            this.form.get('selectedReasonId')!.setValue(null)
        }
        this.form.get('selectedAreaId')!.setValue(null)
        this.form.get('time')!.setValue(null)
    }

    selectAreaStep() {
        this.form.get('step')!.setValue(ModifyBookingStep.SelectArea)
        this.form.get('selectedAreaId')!.setValue(null)
        this.form.get('time')!.setValue(null)
    }

    selectTimeStep() {
        this.form.get('step')!.setValue(ModifyBookingStep.SelectTime)
        if (this.isDurationReasonMode(this.availability)) {
            this.form.get('selectedReasonId')!.setValue(null)
            if (!this.hasFixedAreaSelection()) {
                this.form.get('selectedAreaId')!.setValue(null)
            }
            this.form.get('endTime')!.setValue(null)
        }
        this.form.get('time')!.setValue(null)
    }

    selectDurationStep() {
        this.form.get('step')!.setValue(ModifyBookingStep.SelectDuration)
    }

    joinWaitlistStep(idealTime?: Date) {
        if (idealTime) {
            this.form.get('idealTime')!.setValue(idealTime)
        }
        this.form.get('step')!.setValue(ModifyBookingStep.JoinWaitlist)
    }

    checkAvailabilitySelected() {
        if (!this.form.get('date')!.value) {
            return
        }
        this.loggingService.trackAction(BookingAction.CheckAvailability, {
            partySize: this.form.get('partySize')!.value,
            date: this.form.get('date')!.value,
            idealTime: this.form.get('idealTime')!.value
        })
        this.refreshAvailability()
    }

    private refreshAvailability() {
        this.updateIdealTimeDateAndAdjustToAvailabilitySubscription?.unsubscribe()
        this.getAvailability()
            .pipe(
                takeUntil(this.onDestroy$),
            )
            .subscribe(availability => {
                this.availability = availability
                this.updateMinLargePartySize()
                this.form.get('availability')!.setValue(availability)
                this.goToNextStepAfterGettingAvailability(availability)
            })
    }

    goToNextStepAfterGettingAvailability(availability: Availability) {
        const pinnedEvent = availability.venue.pinnedEvent()
        const shouldStopToLookAtPinnedEvent = pinnedEvent !== null
            && this.preselectedAreaId === undefined
            && this.preselectedEventId === undefined
            && this.restrictedReasonIds === undefined
            && this.bookingDetails === undefined
            && this.initialIdealTime !== undefined
        const hasSetViewPinnedEventFlag = this.hasHadChanceToViewPinnedEvent !== null
        if (!hasSetViewPinnedEventFlag) {
            this.hasHadChanceToViewPinnedEvent = !shouldStopToLookAtPinnedEvent
        } else if (pinnedEvent && this.hasHadChanceToViewPinnedEvent === false) {
            this.hasHadChanceToViewPinnedEvent = true
        }
        if (pinnedEvent && this.hasHadChanceToViewPinnedEvent === false) {
            this.form.get('step')!.setValue(ModifyBookingStep.SelectTableAndDate)
            return
        }
        const ngbDate = this.form.get('date')!.value as NgbDate
        const day = this.ngbDateAdapter.toModel(ngbDate)
        const partySize = this.form.get('partySize')!.value
        const idealTime = this.form.get('idealTime')!.value
        const isLargePartyEnquiry = availability.isLargeParty(
            partySize,
            day,
            this.preselectedEventId
        )
            && availability.venue.enquiriesEnabled
        if (isLargePartyEnquiry) {
            this.form.get('step')!.setValue(ModifyBookingStep.SelectTableAndDate)
            return
        }
        const tableAndDateContextFilled = day && partySize && idealTime
        if (!tableAndDateContextFilled) {
            this.form.get('step')!.setValue(ModifyBookingStep.SelectTableAndDate)
            this.form.get('partySize')!.setValue(availability.defaultReservablePartySize())
            this.form.get('partySize')!.markAsDirty()
            const earliestAvailability = availability.earliestAvailability() ?? null
            if (earliestAvailability && earliestAvailability.options.length > 0) {
                const earliestDateTime = earliestAvailability.bookingSlot.dateTime
                const ngbDate = this.ngbDateAdapter.fromModel(earliestDateTime)
                this.form.get('date')!.setValue(ngbDate)
                this.form.get('date')!.markAsDirty()
                this.form.get('idealTime')!.setValue(this.initialIdealTime ?? earliestDateTime)
                this.form.get('idealTime')!.markAsDirty()
            } else if (!this.form.get('date')!.value || !this.form.get('idealTime')!.value) {
                const fallbackDate = this.nextKnownOpenDateOrTomorrow(day ?? new Date())
                const midday = new Date(fallbackDate)
                midday.setHours(12, 0, 0, 0)
                this.form.get('date')!.setValue(this.ngbDateAdapter.fromModel(midday))
                this.form.get('date')!.markAsDirty()
                this.form.get('idealTime')!.setValue(midday)
                this.form.get('idealTime')!.markAsDirty()
            }
            return
        }
        const alreadySelectedTime = this.form.get('time')!.value
        const selectedReasonId = this.form.get('selectedReasonId')!.value
        if (alreadySelectedTime) {
            if (this.isDurationReasonMode(availability) && !selectedReasonId) {
                this.form.get('step')!.setValue(ModifyBookingStep.SelectDuration)
                return
            }
            if (this.shouldShowOrderSelection(availability)) {
                this.form.get('step')!.setValue(ModifyBookingStep.SelectOrder)
            } else {
                this.form.get('step')!.setValue(ModifyBookingStep.Summary)
            }
            return
        }
        const hasAccessibilityQuestions = availability.venue.hasAccessibilityQuestions()
        const alreadySelectedRestrictions =
            this.form.get('requiresWheelchairAccess')!.value !== null || this.form.get('requiresDogFriendly')!.value !== null || this.form.get('requiresPramAccessible')!.value !== null || this.form.get('requiresHighchairFriendly')!.value !== null
        if (hasAccessibilityQuestions && !alreadySelectedRestrictions && !this.hideDateTimeControls) {
            this.form.get('step')!.setValue(ModifyBookingStep.SelectRestrictions)
            return
        }
        const events = availability.availableEvents()
        const eventsAreConfigured = events.length > 0
        const canSelectEvents = this.form.get('selectedEventId')!.enabled
        const hasPreselectedEvents = this.preselectedEventId !== null
        if (canSelectEvents && hasPreselectedEvents) {
            const preselectedEvent = events.find(event => event.id === this.preselectedEventId)
            if (preselectedEvent) {
                this.eventSelected(preselectedEvent, availability)
                if (this.hideDateTimeControls) {
                    this.autoSelectBookingSlotIfSingleMatch(availability)
                }
                return
            }
        }
        const reasons = availability.venue.reasonsUsedOnDate(day)
        const reasonsAreConfigured = reasons.length > 0
        const isReasonSelectionEnabled = this.form.get('selectedReasonId')!.enabled
        const hasPreselectedReason = this.restrictedReasonIds?.length === 1
        const preselectedReasonId = hasPreselectedReason ? this.restrictedReasonIds![0] : null
        const preselectedReason = preselectedReasonId
            ? reasons.find(reason => reason.id === preselectedReasonId)
            : null
        if (eventsAreConfigured && canSelectEvents && !preselectedReason) {
            this.form.get('step')!.setValue(ModifyBookingStep.SelectBookingType)
            return
        }
        if (!eventsAreConfigured) {
            this.form.get('bookingType')!.setValue(BookingType.Standard)
        }
        
        if (reasonsAreConfigured && isReasonSelectionEnabled) {
            if (preselectedReason) {
                this.form.get('bookingType')!.setValue(BookingType.Standard)
                this.form.get('selectedReasonId')!.setValue(preselectedReason.id)
                this.reasonSelected(preselectedReason, availability)
                return
            } else if (this.isDurationReasonMode(availability)) {
                this.startDurationReasonFlow(availability)
                return
            } else {
                this.form.get('step')!.setValue(ModifyBookingStep.SelectReason)
                return
            }
        }
        const userCanSelectArea = availability.venue.customerCanChooseAreaFor(null)
        this.form.get('areaSelectionIsPossible')!.setValue(userCanSelectArea)
        const hasNotInteractedWithArea = this.form.get('selectedAreaId')!.pristine
        if (userCanSelectArea && this.preselectedAreaId && hasNotInteractedWithArea) {
            const areas = availability.areas
            const preselectedArea = areas.find(area => area.area.id === this.preselectedAreaId)
            if (preselectedArea) {
                this.form.get('selectedAreaId')?.setValue(preselectedArea.area.id)
                this.form.get('selectedAreaId')?.markAsDirty()
                this.areaSelected(preselectedArea.area)
            }
            const idealTime = this.form.get('idealTime')!.value
            if (idealTime) {
                const reasonId = this.form.get('selectedReasonId')!.value
                const eventId = this.form.get('selectedEventId')!.value
                const reason = reasonId ? availability.venue.reasonWithId(reasonId) : null
                const event = eventId ? availability.venue.eventWithId(eventId) : null
        
                const matchingSlot = preselectedArea?.bookingSlots.map(bookingSlot => {
                    return bookingSlot.reservableOptionWithConfiguration(
                        this.form.get('partySize')!.value,
                        reason,
                        event,
                        idealTime
                    )
                }).find(option => option !== null)
                if (matchingSlot) {
                    this.form.get('time')!.setValue(this.ngbTimeAdapter.fromModel(idealTime))
                    this.form.get('time')!.markAsDirty()
                    const bookingSlot = new BookingSlot(idealTime)
                    const duration = matchingSlot.durationMinutes
                    const endTime = bookingSlot.endDateTime(duration)
                    this.form.get('endTime')!.setValue(this.ngbTimeAdapter.fromModel(endTime))
                    this.form.get('endTime')!.markAsDirty()
                    this.form.get('step')!.setValue(ModifyBookingStep.Summary)
                    return
                }
            }
        }
        this.goToAreaSelectionStep(availability)
    }

    restrictionsSelected() {
        const wheelchairAccessControl = this.form.get('requiresWheelchairAccess')!
        if (wheelchairAccessControl.value === null) {
            wheelchairAccessControl.setValue(false)
        }
        const dogFriendlyControl = this.form.get('requiresDogFriendly')!
        if (dogFriendlyControl.value === null) {
            dogFriendlyControl.setValue(false)
        }
        const pramAccessibleControl = this.form.get('requiresPramAccessible')!
        if (pramAccessibleControl.value === null) {
            pramAccessibleControl.setValue(false)
        }
        const highchairFriendlyControl = this.form.get('requiresHighchairFriendly')!
        if (highchairFriendlyControl.value === null) {
            highchairFriendlyControl.setValue(false)
        }
        this.loggingService.trackAction(BookingAction.SelectRestrictions, {
            requiresWheelchairAccess: wheelchairAccessControl.value,
            requiresDogFriendly: dogFriendlyControl.value,
            requiresPramAccessible: pramAccessibleControl.value,
            requiresHighchairFriendly: highchairFriendlyControl.value
        })
        this.getAvailability()
            .pipe(
                takeUntil(this.onDestroy$),
            )
            .subscribe(availability => {
                this.availability = availability
                this.updateMinLargePartySize()
                this.form.get('availability')!.setValue(availability)
                this.goToNextStepAfterGettingAvailability(availability)
            })
    }

    normalBookingTypeSelected(availability: Availability) {
        this.loggingService.trackAction(BookingAction.SelectStandardBooking)
        this.form.get('bookingType')!.setValue(BookingType.Standard)
        this.form.get('selectedEventId')!.setValue(null)
        const ngbDate = this.form.get('date')!.value as NgbDate
        const day = this.ngbDateAdapter.toModel(ngbDate)!
        const reasons = availability.venue.reasonsUsedOnDate(day)
        const reasonsAreConfigured = reasons.length > 0
        if (reasonsAreConfigured) {
            if (this.isDurationReasonMode(availability)) {
                this.startDurationReasonFlow(availability)
                return
            }
            this.form.get('step')!.setValue(ModifyBookingStep.SelectReason)
            return
        }
        const userCanSelectArea = availability.venue.customerCanChooseAreaFor(null)
        this.form.get('areaSelectionIsPossible')!.setValue(userCanSelectArea)
        this.goToAreaSelectionStep(availability)
    }

    eventSelected(event: Event, availability: Availability) {
        this.loggingService.trackAction(BookingAction.SelectEvent, {
            eventId: event.id,
            eventName: event.displayName
        })
        this.form.get('bookingType')!.setValue(BookingType.Event)
        this.form.get('selectedEventId')!.setValue(event.id)
        const userCanSelectArea = availability.venue.customerCanChooseAreaFor(null)
        this.form.get('areaSelectionIsPossible')!.setValue(userCanSelectArea)
        if (event.notReservableOnline) {
            this.form.get('step')!.setValue(ModifyBookingStep.ExplainNonReservableEventBooking)
        } else {
            this.goToAreaSelectionStep(availability)
        }
    }

    reasonSelected(reason: BookingReason, availability: Availability) {
        const isDurationSelection = this.isDurationReasonMode(availability) && this.form.get('time')!.value !== null
        this.loggingService.trackAction(
            isDurationSelection ? BookingAction.SelectDuration : BookingAction.SelectReason,
            {
                reasonId: reason.id,
                reasonName: reason.displayName
            }
        )
        if (isDurationSelection) {
            this.completeDurationSelection(reason, availability)
            return
        }
        const userCanSelectArea = availability.venue.customerCanChooseAreaFor(reason)
        this.form.get('areaSelectionIsPossible')!.setValue(userCanSelectArea)
        this.goToAreaSelectionStep(availability)
    }

    areaSelected(area: Area) {
        this.loggingService.trackAction(BookingAction.SelectArea, {
            areaId: area.id,
            areaName: area.displayName
        })
        this.form.get('step')!.setValue(ModifyBookingStep.SelectTime)
    }

    reset() {
        this.form.reset()
        this.setFormToBookingDetailsIfNotAlreadySet()
    }

    onEnquirySubmitted() {
        // Enquiry was successfully submitted
        // The form will show the success message
    }

    save(availability: Availability) {
        this.loggingService.trackAction(BookingAction.ConfirmBookingOptions)
        this.saveRequested.emit([this.form, availability])
    }

    private getAvailability() {
        const dateTime = this.getFormDate() ?? new Date()
        const requiresWheelchairAccess = this.form.get('requiresWheelchairAccess')!.value
        const requiresDogFriendly = this.form.get('requiresDogFriendly')!.value
        const requiresPramAccessible = this.form.get('requiresPramAccessible')!.value
        const requiresHighchairFriendly = this.form.get('requiresHighchairFriendly')!.value
        const linkExclusiveReasonId = this.bookingDetails?.reasonIsLinkExclusive ? this.bookingDetails.reasonId : null
        let restrictedReasonIds = this.restrictedReasonIds
        if (linkExclusiveReasonId) {
            restrictedReasonIds = [linkExclusiveReasonId]
        }
        this.availability = null
        this.minLargePartySize = null
        this.venueNotInService = false
        return this.availabilityService.getAvailability(
            this.organisationId,
            this.businessId,
            this.venueId,
            dateTime,
            dateTime,
            this.bookingDetails?.bookingId,
            requiresWheelchairAccess ?? undefined,
            requiresDogFriendly ?? undefined,
            requiresPramAccessible ?? undefined,
            requiresHighchairFriendly ?? undefined,
            restrictedReasonIds
        )
            .pipe(
                tap(availability => {
                    this.venueColour = availability.venue.primaryColour
                    this.disablePaymentRelatedOptionsIfDateRestricted()
                    this.disableEventSelectionIfModifyingBooking()
                    this.disableReasonSelectionIfModifyingBooking()
                }),
                catchError(error => {
                    if (isVenueNotInServiceError(error)) {
                        this.venueNotInService = true
                        return EMPTY
                    }
                    return throwError(() => error)
                })
            )
    }

    loadOpenDaysForMonth(dateInMonth: Date) {
        this.getOpenDaysForMonth(dateInMonth)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe()
    }

    private loadInitialOpenDaysThenRefreshAvailability(initialDate: Date) {
        const nextMonthDate = new Date(initialDate.getFullYear(), initialDate.getMonth() + 1, 1)
        forkJoin([
            this.getOpenDaysForMonth(initialDate),
            this.getOpenDaysForMonth(nextMonthDate),
        ])
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
                this.refreshAvailability()
            })
    }

    private getOpenDaysForMonth(dateInMonth: Date) {
        const year = dateInMonth.getFullYear()
        const oneIndexedMonth = dateInMonth.getMonth() + 1
        if (this.openDaysCalendar.isMonthAvailabilityKnown(year, oneIndexedMonth)) {
            return of(undefined)
        }
        this.openDaysCalendar.markMonthRequested(year, oneIndexedMonth)
        const startDate = new Date(year, dateInMonth.getMonth(), 1)
        const endDate = new Date(year, dateInMonth.getMonth() + 1, 0)
        return this.availabilityService.getOpenDays(this.organisationId, this.businessId, this.venueId, startDate, endDate)
            .pipe(
                tap(days => {
                    this.openDaysCalendar.addDays(days)
                }),
                map(() => undefined),
                catchError(() => {
                    this.openDaysCalendar.unmarkMonthRequested(year, oneIndexedMonth)
                    return of(undefined)
                })
            )
    }

    private nextKnownOpenDateOrTomorrow(date: Date): Date {
        const nextOpenDate = this.openDaysCalendar.firstOpenDateAfter(date)
        if (nextOpenDate) {
            return nextOpenDate
        }
        const tomorrow = new Date(date)
        tomorrow.setDate(tomorrow.getDate() + 1)
        return tomorrow
    }

    onDatepickerNavigate(event: { current: { year: number; month: number } | null; next: { year: number; month: number }; preventDefault: () => void }) {
        this.loadOpenDaysForMonth(new Date(event.next.year, event.next.month - 1, 1))
    }

    private setFormToBookingDetailsIfNotAlreadySet() {
        const bookingDetails = this.bookingDetails
        if (!bookingDetails) {
            return
        }
        if (this.form.valid) {
            return
        }
        const bookingType = bookingDetails.eventId ? BookingType.Event : BookingType.Standard
        this.form.setValue({
            availability: null,
            step: ModifyBookingStep.Summary,
            partySize: bookingDetails.partySize,
            idealTime: new Date(bookingDetails.dateTime),
            date: this.ngbDateAdapter.fromModel(bookingDetails.dateTime),
            requiresWheelchairAccess: bookingDetails.requiresWheelchairAccess,
            requiresDogFriendly: bookingDetails.requiresDogFriendly,
            requiresPramAccessible: bookingDetails.requiresPramAccessible,
            requiresHighchairFriendly: bookingDetails.requiresHighchairFriendly,
            selectedAreaId: bookingDetails.areaName !== null ? bookingDetails.areaId : null,
            areaSelectionIsPossible: bookingDetails.areaName !== null,
            bookingType: bookingType,
            selectedEventId: bookingDetails.eventId,
            selectedReasonId: bookingDetails.reasonId,
            time: this.ngbTimeAdapter.fromModel(bookingDetails.dateTime),
            endTime: this.ngbTimeAdapter.fromModel(bookingDetails.endTime),
        })
    }

    private updateMinLargePartySize() {
        const availability = this.availability
        if (!availability) {
            this.minLargePartySize = null
            return
        }
        this.minLargePartySize = availability.largePartySizeThreshold(
            this.formDate,
            this.preselectedEventId
        )
    }

    private getFormDate() {
        const date = this.form.get('date')!.value
        if (!date) {
            return null
        }
        const day = this.ngbDateAdapter.toModel(date)!
        return new Date(
            day.getFullYear(),
            day.getMonth(),
            day.getDate(),
            0,
            0
        )
    }

    private bindFormDateToGettingAvailability() {
        this.form.get('date')!.valueChanges
            .pipe(
                distinctUntilChanged(),
                takeUntil(this.onDestroy$),
            )
            .subscribe(() => {
                const formDate = this.getFormDate()
                if (formDate) {
                    this.loadOpenDaysForMonth(formDate)
                }
                this.goNextStepAfterGettingAvailabilitySubscription?.unsubscribe()
                this.getAvailability()
                    .pipe(
                        takeUntil(this.onDestroy$)
                    )
                    .subscribe(availability => {
                        this.availability = availability
                        this.updateMinLargePartySize()
                        this.form.get('availability')!.setValue(availability)
                        this.updateIdealTimeDateAndAdjustToAvailability(availability)
                    })
            })
    }

    private bindFormTimeToGoingToNextStep() {
        this.form.get('time')!.valueChanges
            .pipe(
                takeUntil(this.onDestroy$)
            )
            .subscribe(time => {
                if (time) {
                    this.loggingService.trackAction(BookingAction.SelectTime, {
                        selectedTime: time,
                    })
                    const availability = this.availability
                    if (availability && this.isDurationReasonMode(availability)) {
                        this.form.get('step')!.setValue(ModifyBookingStep.SelectDuration)
                    } else if (availability && this.shouldShowOrderSelection(availability)) {
                        this.form.get('step')!.setValue(ModifyBookingStep.SelectOrder)
                    } else {
                        this.form.get('step')!.setValue(ModifyBookingStep.Summary)
                    }
                }
            })
    }

    private goToAreaSelectionStep(availability: Availability) {
        const userCanSelectArea = this.form.get('areaSelectionIsPossible')!.value
        if (!userCanSelectArea) {
            this.form.get('step')!.setValue(ModifyBookingStep.SelectTime)
            return
        }
        let areas = availability.areas.map(areaAvailability => areaAvailability.area)
        const selectedEventId = this.form.get('selectedEventId')!.value
        if (selectedEventId) {
            const event = availability.venue.eventWithId(selectedEventId)
            if (event) {
                areas = availability.areasUsedInEvent(event)
            }
        }
        const areaSelectionRequired = areas.length > 1
        if (!areaSelectionRequired) {
            const area = areas[0]
            this.form.get('selectedAreaId')?.setValue(area.id)
            this.form.get('selectedAreaId')?.markAsDirty()
            this.areaSelected(area)
            return
        }
        this.form.get('step')!.setValue(ModifyBookingStep.SelectArea)
    }

    private autoSelectBookingSlotIfSingleMatch(availability: Availability) {
        const eventId = this.form.get('selectedEventId')!.value
        const reasonId = this.form.get('selectedReasonId')!.value
        const partySize = this.form.get('partySize')!.value
        const idealTime = this.form.get('idealTime')!.value
        if (!idealTime) return

        const reason = reasonId ? availability.venue.reasonWithId(reasonId) : null
        const event = eventId ? availability.venue.eventWithId(eventId) : null
        const selectedAreaId = this.form.get('selectedAreaId')!.value
        const areaId = availability.venue.customerCanChooseAreaFor(reason) ? selectedAreaId : null

        const reservableAreas = availability.areas.filter(area => {
            if (areaId) return area.area.id === areaId
            if (reason) return area.isConfiguredForReason(reason)
            return true
        })
        if (reservableAreas.length === 0) return

        const allSlots = reservableAreas
            .flatMap(area => area.bookingSlots)
            .filter(slot => slot.bookingSlot.dateTime.getTime() >= reservableAreas
                .find(a => a.bookingSlots.includes(slot))!.area.cutOffTime.getTime())

        const reservableSlots = allSlots.filter(slot =>
            slot.reservableOptionWithConfiguration(partySize, reason, event, slot.bookingSlot.dateTime) !== null
        )

        const uniqueSlotTimes = [...new Set(reservableSlots.map(s => s.bookingSlot.dateTime.getTime()))]
        if (uniqueSlotTimes.length !== 1) return

        const slotTime = reservableSlots[0].bookingSlot.dateTime
        const timesMatch = slotTime.getHours() === idealTime.getHours()
            && slotTime.getMinutes() === idealTime.getMinutes()
        if (!timesMatch) return

        const option = reservableSlots[0].reservableOptionWithConfiguration(partySize, reason, event, slotTime)!
        const endTime = reservableSlots[0].bookingSlot.endDateTime(option.durationMinutes)

        const reservableArea = reservableAreas.find(a => a.bookingSlots.includes(reservableSlots[0]))
        if (reservableArea && !areaId) {
            this.form.get('selectedAreaId')?.setValue(reservableArea.area.id)
            this.form.get('selectedAreaId')?.markAsDirty()
        }

        this.form.get('time')!.setValue(this.ngbTimeAdapter.fromModel(slotTime))
        this.form.get('time')!.markAsDirty()
        this.form.get('endTime')!.setValue(this.ngbTimeAdapter.fromModel(endTime))
        this.form.get('endTime')!.markAsDirty()
    }

    get haveBookingDetailsChanged() {
        const bookingDetails = this.bookingDetails
        if (!bookingDetails) {
            return false
        }
        const date = this.getFormDate()
        if (!date) {
            return false
        }
        const ngbTime = this.form.get('time')!.value
        if (!ngbTime) {
            return false
        }
        const time = this.ngbTimeAdapter.toModel(ngbTime)
        if (!time) {
            return false
        }
        const dateTime = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            time.getHours(),
            time.getMinutes(),
            0
        )
        if (dateTime.getTime() !== bookingDetails.dateTime.getTime()) {
            return true
        }
        const areaId = this.form.get('selectedAreaId')!.value
        if (areaId !== bookingDetails.areaId) {
            return true
        }
        const reasonId = this.form.get('selectedReasonId')!.value
        if (reasonId !== bookingDetails.reasonId) {
            return true
        }
        const partySize = this.form.get('partySize')!.value
        if (partySize !== bookingDetails.partySize) {
            return true
        }
        return false
    }

    private disablePaymentRelatedOptionsIfDateRestricted() {
        const bookingDetails = this.bookingDetails
        if (!bookingDetails) {
            return
        }
        const paymentRelatedControls = [
            'partySize',
            'date',
        ]
        if (bookingDetails.isDateRestricted) {
            paymentRelatedControls.forEach(controlName => {
                this.form.get(controlName)!.disable()
            })
        } else {
            paymentRelatedControls.forEach(controlName => {
                this.form.get(controlName)!.enable()
            })
        }
    }

    private disableEventSelectionIfModifyingBooking() {
        const bookingDetails = this.bookingDetails
        if (!bookingDetails) {
            this.form.get('selectedEventId')!.enable()
        } else {
            this.form.get('selectedEventId')!.disable()
        }
    }

    private disableReasonSelectionIfModifyingBooking() {
        const bookingDetails = this.bookingDetails
        if (!bookingDetails) {
            this.form.get('selectedReasonId')!.enable()
        } else {
            this.form.get('selectedReasonId')!.disable()
        }
    }

    private updateIdealTimeDateAndAdjustToAvailability(availability: Availability) {
        const idealTime = this.form.get('idealTime')!.value
        const ngbDate = this.form.get('date')!.value as NgbDate
        const date = this.ngbDateAdapter.toModel(ngbDate)
        if (!date) {
            return
        }
        const closestTime = availability.closestIntervalIdealTime(idealTime, date)
        if (closestTime) {
            this.form.get('idealTime')!.setValue(closestTime)
        }
    }

    private shouldShowOrderSelection(availability: Availability): boolean {
        const reasonId = this.form.get('selectedReasonId')!.value
        if (!reasonId) {
            return false
        }
        const menu = availability.bookingUpsellMenu(reasonId)
        return menu !== null && !menu.isPaid
    }

    canSelectTimeOptions(availability: Availability): boolean {
        const partySize = this.form.get('partySize')!.value
        const reasonId = this.form.get('selectedReasonId')!.value
        const eventId = this.form.get('selectedEventId')!.value
        if (!this.isDurationReasonMode(availability) || reasonId !== null) {
            return availability.isConfigurationReservable(
                partySize,
                reasonId,
                eventId
            )
        }
        const areaId = this.form.get('selectedAreaId')!.value as string | null
        const reason = reasonId ? availability.venue.reasonWithId(reasonId) : null
        if (areaId && availability.venue.customerCanChooseAreaFor(reason)) {
            return availability.areaIsReservableWithAnyReason(
                areaId,
                partySize,
                eventId
            )
        }
        return availability.isConfigurationReservableWithAnyReason(
            partySize,
            eventId
        )
    }

    selectedReasonLabel(availability: Availability): string {
        if (this.isDurationReasonMode(availability)) {
            return 'Booking duration'
        }
        return 'Reason for your visit'
    }

    selectReasonOrDurationStep(availability: Availability) {
        if (this.isDurationReasonMode(availability)) {
            this.selectDurationStep()
        } else {
            this.selectReasonStep()
        }
    }

    isDurationReasonMode(availability: Availability | null): boolean {
        if (!availability) {
            return false
        }
        const bookingType = this.form?.get('bookingType')?.value
        return availability.venue.usesReasonsAsDurations && bookingType !== BookingType.Event
    }

    private startDurationReasonFlow(availability: Availability) {
        this.form.get('selectedReasonId')!.setValue(null)
        this.form.get('areaSelectionIsPossible')!.setValue(false)
        const fixedArea = availability.venue.customerCanChooseAreaFor(null) && this.hasFixedAreaSelection()
            ? availability.areas.find(a => a.area.id === this.preselectedAreaId)
            : null
        this.form.get('selectedAreaId')!.setValue(fixedArea?.area.id ?? null)
        if (fixedArea) {
            this.form.get('selectedAreaId')!.markAsDirty()
        }
        this.form.get('step')!.setValue(ModifyBookingStep.SelectTime)
    }

    private completeDurationSelection(reason: BookingReason, availability: Availability) {
        const partySize = this.form.get('partySize')!.value as number
        const eventId = this.form.get('selectedEventId')!.value as string | null
        const slotDateTime = this.selectedBookingSlotDateTime(availability)
        if (!slotDateTime) {
            return
        }
        const selectedAreaId = availability.venue.customerCanChooseAreaFor(reason)
            ? this.form.get('selectedAreaId')!.value as string | null
            : null
        const reservableArea = selectedAreaId
            ? availability.areas.find(areaAvailability => areaAvailability.area.id === selectedAreaId)?.area ?? null
            : availability.firstReservableAreaWithConfigurationAtDateTime(
                partySize,
                reason.id,
                eventId,
                slotDateTime
            )
        const reservableOption = reservableArea
            ? availability.reservableOptionWithConfiguration(
                reservableArea.id,
                partySize,
                reason.id,
                eventId,
                slotDateTime
            )
            : null
        if (!reservableArea || !reservableOption) {
            return
        }
        this.form.get('areaSelectionIsPossible')!.setValue(false)
        this.form.get('selectedAreaId')!.setValue(reservableArea.id)
        this.form.get('selectedAreaId')!.markAsDirty()
        const endTime = new BookingSlot(slotDateTime).endDateTime(reservableOption.durationMinutes)
        this.form.get('endTime')!.setValue(this.ngbTimeAdapter.fromModel(endTime))
        this.form.get('endTime')!.markAsDirty()
        if (this.shouldShowOrderSelection(availability)) {
            this.form.get('step')!.setValue(ModifyBookingStep.SelectOrder)
        } else {
            this.form.get('step')!.setValue(ModifyBookingStep.Summary)
        }
    }

    private selectedBookingSlotDateTime(availability: Availability): Date | null {
        const ngbDate = this.form.get('date')!.value as NgbDate
        const ngbTime = this.form.get('time')!.value
        if (!ngbDate || !ngbTime) {
            return null
        }
        const date = this.ngbDateAdapter.toModel(ngbDate)
        const time = this.ngbTimeAdapter.toModel(ngbTime)
        if (!date || !time) {
            return null
        }
        return availability.getDateTimeForBookingSlotDateAndTime(date, time)
    }

    private hasFixedAreaSelection(): boolean {
        return this.preselectedAreaId !== undefined && this.preselectedAreaId !== null
    }

    protected readonly DurationUnit = DurationUnit
    protected readonly BookingType = BookingType
    protected readonly environment = environment
    protected readonly Helper = Helper
}
